import React, { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";

const GIFT_STORAGE_KEY = "orat_gift_details";

export const getStoredGiftDetails = () => {
  try {
    const raw = sessionStorage.getItem(GIFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearStoredGiftDetails = () => {
  sessionStorage.removeItem(GIFT_STORAGE_KEY);
};

/** Shape sent with place_order when the gift checkbox is active. */
export const buildGiftOrderPayload = (details) => {
  if (!details) return null;
  const recipientName = (details.recipientName || "").trim();
  const message = (details.message || "").trim();
  const senderName = (details.senderName || "").trim();
  if (!recipientName && !message && !senderName) return null;

  return {
    is_gift: 1,
    gift_recipient_name: recipientName,
    gift_message: message,
    gift_sender_name: senderName,
  };
};

function GiftBoxesIllustration() {
  return (
    <svg
      className="GiftModal__art"
      viewBox="0 0 320 360"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g>
        <ellipse cx="210" cy="78" rx="28" ry="36" fill="#B8E4E0" />
        <path d="M210 114 Q205 160 220 210" stroke="#9bbbb7" strokeWidth="1.2" fill="none" />
        <ellipse cx="248" cy="58" rx="24" ry="32" fill="#F7E6A3" />
        <path d="M248 90 Q242 140 230 200" stroke="#c4b57a" strokeWidth="1.2" fill="none" />
        <ellipse cx="278" cy="95" rx="22" ry="30" fill="#F5B8C8" />
        <path d="M278 125 Q268 165 245 215" stroke="#c98a9a" strokeWidth="1.2" fill="none" />
        <ellipse cx="175" cy="55" rx="20" ry="28" fill="#A9D9D4" />
        <path d="M175 83 Q180 140 210 205" stroke="#8fb5b0" strokeWidth="1.2" fill="none" />
      </g>

      <g>
        <rect x="140" y="220" width="120" height="95" rx="6" fill="#F6E39A" />
        <rect x="188" y="220" width="24" height="95" fill="#F2D36B" />
        <rect x="140" y="255" width="120" height="22" fill="#F2D36B" />
        <path d="M200 220 C185 200 170 205 176 220" fill="#FFF3C2" stroke="#E8C85A" />
        <path d="M200 220 C215 200 230 205 224 220" fill="#FFF3C2" stroke="#E8C85A" />
      </g>

      <g>
        <rect x="95" y="250" width="78" height="70" rx="5" fill="#B7E0DB" />
        <rect x="128" y="250" width="14" height="70" fill="#8FCFC8" />
        <rect x="95" y="276" width="78" height="16" fill="#8FCFC8" />
        <path d="M135 250 C124 236 114 240 118 250" fill="#D9F3F0" stroke="#7EC2BA" />
        <path d="M135 250 C146 236 156 240 152 250" fill="#D9F3F0" stroke="#7EC2BA" />
      </g>

      <g>
        <rect x="55" y="275" width="55" height="50" rx="4" fill="#F4B6C6" />
        <rect x="78" y="275" width="10" height="50" fill="#E994AC" />
        <rect x="55" y="294" width="55" height="12" fill="#E994AC" />
        <path d="M83 275 C75 265 68 268 71 275" fill="#FFD6E1" stroke="#E28AA1" />
        <path d="M83 275 C91 265 98 268 95 275" fill="#FFD6E1" stroke="#E28AA1" />
      </g>
    </svg>
  );
}

function GiftModal({ show, onClose, onSave, initialValues }) {
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [senderName, setSenderName] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!show) return;
    setRecipientName(initialValues?.recipientName || "");
    setMessage(initialValues?.message || "");
    setSenderName(initialValues?.senderName || "");
    setErrors({});
    setSubmitted(false);
  }, [show, initialValues]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [show, onClose]);

  if (!show) return null;

  const validate = (values) => {
    const next = {};
    const recipient = values.recipientName.trim();
    const msg = values.message.trim();
    const sender = values.senderName.trim();

    if (!recipient || recipient.length > 50) {
      next.recipientName = "Recipient's Name should be between 0-50 characters.";
    }
    if (!msg || msg.length > 500) {
      next.message = "Message should be between 0-500 characters.";
    }
    if (!sender || sender.length > 50) {
      next.senderName = "Sender's Name should be between 0-50 characters.";
    }
    return next;
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSubmitted(true);

    const payload = {
      recipientName: recipientName.trim(),
      message: message.trim(),
      senderName: senderName.trim(),
    };
    const nextErrors = validate(payload);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    sessionStorage.setItem(GIFT_STORAGE_KEY, JSON.stringify(payload));
    onSave?.(payload);
  };

  return (
    <div className="GiftModalOverlay" onClick={onClose} role="presentation">
      <div
        className="GiftModal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gift-modal-title"
      >
        <button type="button" className="GiftModal__close" onClick={onClose} aria-label="Close">
          <IoClose size={20} />
        </button>

        <div className="GiftModal__body">
          <div className="GiftModal__formCol">
            <h2 id="gift-modal-title" className="GiftModal__title">
              SEND A GIFT TO YOUR LOVED ONES
            </h2>
            <p className="GiftModal__subtitle">
              We&apos;ll Gift wrap it and include a special note with your order.
            </p>

            <form onSubmit={handleSave} noValidate>
              <div className="GiftModal__field">
                <input
                  type="text"
                  className={`GiftModal__input ${submitted && errors.recipientName ? "is-invalid" : ""}`}
                  placeholder="Recipient's Name"
                  value={recipientName}
                  maxLength={50}
                  onChange={(e) => {
                    setRecipientName(e.target.value);
                    if (submitted) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.recipientName;
                        return next;
                      });
                    }
                  }}
                />
                {submitted && errors.recipientName && (
                  <p className="GiftModal__hint is-error">{errors.recipientName}</p>
                )}
              </div>

              <div className="GiftModal__field">
                <textarea
                  className={`GiftModal__input GiftModal__textarea ${submitted && errors.message ? "is-invalid" : ""}`}
                  placeholder="Your Message"
                  value={message}
                  maxLength={500}
                  rows={4}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (submitted) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.message;
                        return next;
                      });
                    }
                  }}
                />
                {submitted && errors.message && (
                  <p className="GiftModal__hint is-error">{errors.message}</p>
                )}
              </div>

              <div className="GiftModal__field">
                <input
                  type="text"
                  className={`GiftModal__input ${submitted && errors.senderName ? "is-invalid" : ""}`}
                  placeholder="Sender's Name"
                  value={senderName}
                  maxLength={50}
                  onChange={(e) => {
                    setSenderName(e.target.value);
                    if (submitted) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.senderName;
                        return next;
                      });
                    }
                  }}
                />
                {submitted && errors.senderName && (
                  <p className="GiftModal__hint is-error">{errors.senderName}</p>
                )}
              </div>

              <button type="submit" className="GiftModal__submit">
                SAVE AND CONTINUE
              </button>
            </form>
          </div>

          <div className="GiftModal__artCol">
            <GiftBoxesIllustration />
          </div>
        </div>
      </div>
    </div>
  );
}

export default GiftModal;
